import { Vector2, Vector3 } from 'three';
import { ReferencePlane } from '../../reference';
import { PointsMaterialFactory } from '../../material';
import {
    SketchArcObject,
    SketchLineObject,
    SketchPointKind,
    SketchPointObject,
} from '../../mesh';
import { CoverType } from './type';

/**
 * 等距实体顶端加盖的创建参数。
 */
export interface SketchOffsetCoverCreationOptions {
    /** 盖类型（圆弧盖为半圆胶囊封口，直线盖为线段）。 */
    readonly coverType: CoverType;
    /** 创建时所属参考面。 */
    readonly referencePlane: ReferencePlane;
    /** 盖端点 A：复用相邻实体（源链或偏移链）的自由端点控制点对象，实现天然重合。 */
    readonly startPoint: SketchPointObject;
    /** 盖端点 B：复用相邻实体（源链或偏移链）的自由端点控制点对象，实现天然重合。 */
    readonly endPoint: SketchPointObject;
    /** 圆弧盖弯曲外侧方向（世界坐标方向，胶囊形鼓包朝向）；仅圆弧盖使用，缺省时默认顺时针。 */
    readonly outwardWorld?: Vector3;
}

/**
 * 等距实体「顶端加盖」的盖子实体工厂。
 *
 * 职责：
 * 1. 为开环等距链的每个自由端生成封口实体（直线盖/半圆弧盖）；
 * 2. 盖子端点直接复用相邻实体的端点控制点对象（共享即天然重合，不额外挂重合约束）；
 * 3. 圆弧盖按「胶囊形」规则计算半圆几何：圆心为两端点中点，弯曲方向朝链外侧；
 * 4. 提供尺寸联动重算的几何更新入口（{@link updateArcCoverGeometry}），
 *    供 OffsetChainDerivedSyncService 在距离尺寸改值时重建盖子。
 */
export class SketchOffsetCoverFactory {
    /**
     * 创建盖子实体（直线盖或半圆弧盖）。
     *
     * 说明：
     * 1. 直线盖直接以两端点对象构造 SketchLineObject（共享端点即天然重合）；
     * 2. 圆弧盖圆心为两端点中点（新建独立控制点并挂载为弧子节点），
     *    弯曲方向由 outwardWorld 决定（胶囊外侧），端点同样共享相邻实体控制点；
     * 3. 盖子实体不额外创建重合/相切关系，对齐 SOLIDWORKS 顶端加盖行为。
     *
     * @param options 创建参数。
     * @returns 盖子实体（直线盖或圆弧盖）。
     */
    public static createCoverEntity(options: SketchOffsetCoverCreationOptions): SketchLineObject | SketchArcObject {
        const { coverType, referencePlane, startPoint, endPoint, outwardWorld } = options;

        if (coverType === 'straight line') {
            // 直线盖：直接连接两端点，端点共享相邻实体控制点即天然重合
            return new SketchLineObject({
                referencePlane,
                startPoint,
                endPoint,
                isInfiniteLength: false,
                isDrawingPreviewLine: false,
                isAuxiliaryObject: false,
            });
        }

        // 圆弧盖：半圆，弯曲方向朝胶囊外侧
        const clockwise = SketchOffsetCoverFactory.resolveSemiCircleClockwise(
            referencePlane,
            startPoint.position,
            endPoint.position,
            outwardWorld,
        );
        const { centerWorld, radius, startAngle, endAngle } = SketchOffsetCoverFactory.computeSemiCircleDefinition(
            referencePlane,
            startPoint.position,
            endPoint.position,
            clockwise,
        );
        const centerPoint = new SketchPointObject({
            position: centerWorld,
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });

        return new SketchArcObject({
            referencePlane,
            definition: { centerWorld, radius, startAngle, endAngle, clockwise, segments: 64 },
            startPoint,
            endPoint,
            centerPoint,
        });
    }

    /**
     * 按当前端点位置重建圆弧盖几何（距离尺寸改值联动）。
     *
     * 说明：
     * 1. 保留创建时的旋转方向（胶囊弯曲侧不变），圆心=两端点中点、半径=弦长一半；
     * 2. 圆心控制点静默移动；端点由链写回（syncPositionSilently）维护，这里
     *    只按「求解终态」语义刷新弧几何（applySolvedArcDefinition 不反向派生控制点），
     *    避免 updateArcDefinition 的“定义派生控制点”反向微调共享端点位置。
     *
     * @param cover 圆弧盖实体。
     * @param startPoint 盖端点 A（当前最新位置）。
     * @param endPoint 盖端点 B（当前最新位置）。
     */
    public static updateArcCoverGeometry(
        cover: SketchArcObject,
        startPoint: SketchPointObject,
        endPoint: SketchPointObject,
    ): void {
        // 保留创建时的旋转方向，按当前端点位置重建半圆定义
        const current = cover.getArcDefinition();
        const { centerWorld, radius, startAngle, endAngle } = SketchOffsetCoverFactory.computeSemiCircleDefinition(
            cover.referencePlane,
            startPoint.position,
            endPoint.position,
            current.clockwise,
        );

        cover.getCenterPoint().syncPositionSilently(centerWorld);
        cover.applySolvedArcDefinition({
            centerWorld,
            radius,
            startAngle,
            endAngle,
            clockwise: current.clockwise,
            segments: current.segments,
        });
    }

    /**
     * 计算半圆盖的弧定义：圆心为两端点中点，半径为弦长一半。
     *
     * 说明：角度在参考面局部 2D 坐标系下计算；半圆扫掠方向遵循
     * SketchArcObject 的采样语义（顺时针从 startAngle 递减到 endAngle，
     * 逆时针从 startAngle 递增到 endAngle）。
     * 公开原因：顶端加盖预览（SketchIsometricEntitiesPreview.createCoverPreview）
     * 只持有端点世界坐标，直接复用本方法计算采样所需的半圆定义，
     * 避免预览与落地盖子几何口径分叉。
     *
     * @param referencePlane 参考面。
     * @param startWorld 盖端点 A 世界坐标。
     * @param endWorld 盖端点 B 世界坐标。
     * @param clockwise 是否顺时针（决定扫掠方向）。
     * @returns 弧定义参数（圆心世界坐标/半径/起始角/终止角）。
     */
    public static computeSemiCircleDefinition(
        referencePlane: ReferencePlane,
        startWorld: Vector3,
        endWorld: Vector3,
        clockwise: boolean,
    ): { centerWorld: Vector3; radius: number; startAngle: number; endAngle: number } {
        const centerWorld = startWorld.clone().add(endWorld).multiplyScalar(0.5);
        const radius = startWorld.distanceTo(endWorld) * 0.5;
        const centerLocal = referencePlane.worldToLocal(centerWorld);
        const startLocal = referencePlane.worldToLocal(startWorld);
        const startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
        // 半圆扫掠：顺时针递减 π，逆时针递增 π
        const endAngle = clockwise ? startAngle - Math.PI : startAngle + Math.PI;
        return { centerWorld, radius, startAngle, endAngle };
    }

    /**
     * 根据胶囊外侧提示确定半圆盖的旋转方向（顺时针/逆时针）。
     *
     * 说明：
     * 1. 以「圆心→起点」向量为基准，半圆有两个候选（逆时针中点方向 / 顺时针中点方向）；
     * 2. 将 outwardWorld 投影到参考面局部 2D，选择与外侧提示同侧的候选，
     *    保证盖的鼓包朝向链端外侧（胶囊形封口）；
     * 3. 外侧提示缺失或退化（零向量）时默认顺时针，保证几何闭合。
     * 公开原因：顶端加盖预览（SketchIsometricEntitiesPreview.createCoverPreview）
     * 只持有端点世界坐标，直接复用本方法确定采样方向。
     *
     * @param referencePlane 参考面。
     * @param startWorld 盖端点 A 世界坐标。
     * @param endWorld 盖端点 B 世界坐标。
     * @param outwardWorld 胶囊外侧方向（世界坐标）。
     * @returns true 表示顺时针，false 表示逆时针。
     */
    public static resolveSemiCircleClockwise(
        referencePlane: ReferencePlane,
        startWorld: Vector3,
        endWorld: Vector3,
        outwardWorld: Vector3 | undefined,
    ): boolean {
        const centerWorld = startWorld.clone().add(endWorld).multiplyScalar(0.5);
        const centerLocal = referencePlane.worldToLocal(centerWorld);
        const startLocal = referencePlane.worldToLocal(startWorld);
        // 从圆心指向起点的局部方向
        const fromCenter = new Vector2(startLocal.x - centerLocal.x, startLocal.y - centerLocal.y);
        if (fromCenter.lengthSq() < 1e-12) {
            // 两端点重合（退化）：任意方向均可，默认顺时针
            return true;
        }

        let outwardLocal: Vector2;
        if (outwardWorld) {
            // 外侧方向投影到局部（用起点处附加 outwardWorld 的差分近似平面投影）
            outwardLocal = referencePlane
                .worldToLocal(startWorld.clone().add(outwardWorld))
                .sub(referencePlane.worldToLocal(startWorld));
        } else {
            outwardLocal = new Vector2(0, 0);
        }
        if (outwardLocal.lengthSq() < 1e-12) {
            // 外侧提示缺失/退化：默认顺时针，保证几何闭合
            return true;
        }

        // 两个候选半圆中点方向：起点向量分别逆时针 / 顺时针旋转 90°
        const ccwMid = new Vector2(-fromCenter.y, fromCenter.x);
        const cwMid = new Vector2(fromCenter.y, -fromCenter.x);
        // 弯曲方向取与外侧提示同侧者
        return cwMid.dot(outwardLocal) > ccwMid.dot(outwardLocal);
    }
}
